import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import * as Sentry from "@sentry/react";
import { ApiProvider } from "@waytide/ui";
import { App } from "./App";
import { webApi } from "./modules/api/waytideApi";
import { updateFavicon } from "./modules/theme/favicon";
import { initSentry } from "./modules/observability/sentry";
import { initPostHog } from "./modules/observability/posthog";
import "./index.css";
// The shared miniapp/channels styling (token-pure, reduced-motion-correct). One
// import so every @waytide/ui surface this app renders is styled.
import "@waytide/ui/styles.css";
// The P0a token spine (appearance-reactive `--waytide-sys-*` custom props,
// tokens-only — see the Vite alias). Loaded after index.css; the command-center
// surfaces consume `var(--waytide-sys-*)`; `[data-appearance]` on <html> drives
// them.
import "@waytide-tokens.css";
// Unify the soul: re-point the semantic surface/text tokens onto the washed
// receipt paper for EVERY theme, so every surface (not just the hand-built ones)
// shares one paper across all 40 themes. Loaded right after the spine so it wins
// by source order. See the file header for the cascade rationale.
import "./styles/receipt-soul.css";
// Document-level View Transitions for the command center (token-pure; consumes
// the spine's motion tokens). Loaded after the spine so the vars resolve.
import "./styles/view-transitions.css";

initSentry();
initPostHog();

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <Sentry.ErrorBoundary
      fallback={({ error }) => (
        <div className="flex min-h-screen items-center justify-center px-6 text-center">
          <div className="max-w-md">
            <h1 className="text-sm font-semibold text-primary">
              Something broke.
            </h1>
            <p className="mt-2 text-xs text-muted">
              The crash was reported. Refresh to retry.
            </p>
            <pre className="mt-4 max-h-48 overflow-auto border border-edge bg-surface-raised px-3 py-2 text-left font-code text-[0.625rem] text-faint">
              {error instanceof Error ? error.message : String(error)}
            </pre>
          </div>
        </div>
      )}
    >
      {/* Inject the web-wired data client (localStorage + VITE_API_URL +
          PostHog) into the shared @waytide/ui surface at the app edge.
          Every shared hook reads it via useApi(). */}
      <ApiProvider api={webApi}>
        <App />
      </ApiProvider>
    </Sentry.ErrorBoundary>
  </StrictMode>
);

// Paint the tab favicon in the active theme's ink on load; the theme controls
// repaint it on every change so the icon tracks the theme in lockstep.
updateFavicon();
